import { DateTime } from 'luxon'
import {
  formatCellValue,
  type FormatOptions,
  type ReportAccumulator,
  type ReportColumnDefinition,
  type ReportColumnType,
  type ReportColumnValue,
  type ReportFormatters,
} from '@terros/common'

export const isQuantitativeReportColumn = (column: Pick<ReportColumnDefinition, 'formatters' | 'type'>): boolean => {
  if (column.type === 'number') return true
  return column.type === 'timestamp' && (column.formatters?.includes('daysSince') ?? false)
}

const formatHandlers = {
  formatTimestamp: (ms: number) => DateTime.fromMillis(ms).toLocaleString(DateTime.DATETIME_SHORT),
  formatTimeOfDay: (seconds: number) => DateTime.now().startOf('day').plus({ second: seconds }).toFormat('hh:mm a'),
}

const getFormatOptions = (): FormatOptions => ({
  ...formatHandlers,
  now: DateTime.now().toMillis(),
})

export const formatReportValue = (
  columnType: ReportColumnType,
  value: ReportColumnValue,
  formatters: ReportFormatters[] | undefined
): string | number => {
  return formatCellValue(columnType, value, formatters, getFormatOptions())
}

export const toReadableAccumulator = (accumulator?: ReportAccumulator): string | undefined => {
  switch (accumulator) {
    case 'sum':
      return 'Sum of all values'
    case 'avg':
      return 'Average of all values'
    case 'min':
      return 'Minimum value'
    case 'max':
      return 'Maximum value'
    case 'median':
      return 'Median value'
    case 'distinct':
      return 'Exact total calculation'
    case 'query':
      return 'Custom calculation'
    default:
      return undefined
  }
}

export const formatReportNumber = (value: number, formatters: ReportFormatters[] | undefined): string | number => {
  return formatCellValue('number', value, formatters, getFormatOptions())
}

export const formatReportTimestamp = (value: number, formatters: ReportFormatters[] | undefined): string => {
  const result = formatCellValue('timestamp', value, formatters, getFormatOptions())
  return String(result)
}
